import React, { FunctionComponent } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  Divider,
  Form,
  Input,
  Typography,
  Select,
  InputNumber,
  Space,
} from 'antd';
import { MailOutlined, LockOutlined } from '@ant-design/icons';
import * as styles from './Register.module.scss';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '~/hooks/useQuery';
import { AppStore, userActions, prefixes, UserType } from '@akademiasaas/shared';
import { useSelector } from 'react-redux';
import { getUserIp } from '~/pages/Auth/helpers/checkUserIp';
import { useAppDispatch } from '~/initializeStore';
import { useLoading } from '~/hooks/useLoading';
import { v4 as uuid } from 'uuid';

interface OwnProps {}

type Props = OwnProps;
type AnalyticsWindow = Window & {
  fbq?: (...args: unknown[]) => void;
  gtag?: (...args: unknown[]) => void;
  oaiq?: (...args: unknown[]) => void;
};

const bigInput: React.CSSProperties = { height: 48, borderRadius: 12, fontSize: 15.5 };
const prefixIconStyle: React.CSSProperties = { color: '#8a8f9c', marginRight: 8 };
const boldLabel = (text: string) => <span style={{ fontWeight: 600 }}>{text}</span>;
const getCookie = (name: string) =>
  document.cookie
    .split('; ')
    .find((cookie) => cookie.startsWith(`${name}=`))
    ?.slice(name.length + 1) || null;

interface RegisterFormModel {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  passwordConfirmation: string;
  termsAndPrivacyPolicy: boolean;
  phoneNumberPrefix: string;
  phoneNumber: string | null;
  userType: UserType;
}

export const Register: FunctionComponent<Props> = () => {
  const { t, i18n } = useTranslation(['auth', 'common', 'checkout']);
  const { registerError } = useSelector((store: AppStore) => store.user);
  const isEmailTakenError = registerError === 'auth/email-already-in-use';
  const [loading, startLoading, stopLoading] = useLoading();
  const [form] = Form.useForm();
  const query = useQuery();
  const code = query.get('code') || '';
  const email = query.get('email') || '';
  const referralCode = query.get('ref') || '';
  const dispatch = useAppDispatch();
  const phoneNumber = Form.useWatch('phoneNumber', form);

  // Store referral code in localStorage when present
  React.useEffect(() => {
    if (referralCode) {
      localStorage.setItem('referralCode', referralCode);
    }
  }, [referralCode]);

  const handleSubmitRegisterForm = async (data: RegisterFormModel) => {
    startLoading();
    const ip = await getUserIp();
    const storedReferralCode = localStorage.getItem('referralCode') || undefined;
    const registrationEventId = uuid();

    await dispatch(
      userActions.signUpUser(
        {
          ...data,
          ip,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          lang: i18n.language,
          phoneNumber: data.phoneNumber ? `${data.phoneNumberPrefix}${data.phoneNumber}` : null,
          referralCode: storedReferralCode,
          userType: data.userType,
          registrationTracking: {
            eventId: registrationEventId,
            eventTime: Math.floor(Date.now() / 1000),
            sourceUrl: `${window.location.origin}${window.location.pathname}`,
            userAgent: navigator.userAgent,
            fbp: getCookie('_fbp'),
            fbc: getCookie('_fbc'),
          },
        },
        () => {
          const analyticsWindow = window as AnalyticsWindow;

          // Track successful registration with Meta Pixel
          if (analyticsWindow.fbq) {
            analyticsWindow.fbq(
              'track',
              'CompleteRegistration',
              {},
              { eventID: registrationEventId }
            );
          }

          // Track successful registration with Google Analytics
          if (analyticsWindow.gtag) {
            analyticsWindow.gtag('event', 'sign_up', { method: 'email' });
          }

          // Track successful registration with OpenAI Ads Measurement Pixel
          if (analyticsWindow.oaiq) {
            analyticsWindow.oaiq(
              'measure',
              'registration_completed',
              { type: 'customer_action' },
              { event_id: registrationEventId }
            );
          }

          // Clear referral code after successful registration
          localStorage.removeItem('referralCode');
          stopLoading();
        },
        () => {
          stopLoading();
        }
      )
    );
  };

  return (
    <div className={styles.container}>
      <div className={styles.formContainer}>
        <Typography.Title level={4} className={styles.header}>
          {t<string>('registerPanel')}
        </Typography.Title>
        <Typography.Paragraph type="secondary" className={styles.subtitle}>
          {t<string>('registerSubtitle')}
        </Typography.Paragraph>
        {registerError && !isEmailTakenError ? (
          <Alert
            className={styles.alert}
            type="error"
            title={t<string>(`common:firebaseAuthErrors.${registerError}`, {
              defaultValue: t<string>('common:firebaseAuthErrors.default'),
            })}
          />
        ) : null}
        <Form<RegisterFormModel>
          name="register-form"
          layout="vertical"
          requiredMark={false}
          form={form}
          onFinish={handleSubmitRegisterForm}
          className={styles.form}
          size="large"
          validateTrigger={['onSubmit', 'onBlur']}
          initialValues={{
            code,
            email,
            termsAndPrivacyPolicy: false,
            phoneNumber: null,
            phoneNumberPrefix: '+48',
          }}
        >
          <Form.Item
            name="firstName"
            label={boldLabel(t<string>('firstName'))}
            rules={[
              {
                required: true,
                message: t<string>('common:validationErrors.fieldIsRequired'),
              },
            ]}
          >
            <Input autoFocus style={bigInput} />
          </Form.Item>

          <Form.Item
            name="lastName"
            label={boldLabel(t<string>('lastName'))}
            rules={[
              {
                required: true,
                message: t<string>('common:validationErrors.fieldIsRequired'),
              },
            ]}
          >
            <Input style={bigInput} />
          </Form.Item>


          <Form.Item
            name="email"
            label={boldLabel(t<string>('login'))}
            rules={[
              {
                required: true,
                message: t<string>('common:validationErrors.fieldIsRequired'),
              },
              {
                type: 'email',
                message: t<string>('common:validationErrors.wrongEmail'),
              },
            ]}
            validateStatus={isEmailTakenError ? 'error' : undefined}
            help={isEmailTakenError ? t<string>('common:firebaseAuthErrors.auth/email-already-in-use') : undefined}
          >
            <Input
              prefix={<MailOutlined style={prefixIconStyle} />}
              placeholder={t<string>('emailPlaceholder')}
              style={bigInput}
            />
          </Form.Item>

          <Form.Item
            name="userType"
            label={boldLabel(t<string>('userType'))}
            rules={[
              {
                required: true,
                message: t<string>('common:validationErrors.fieldIsRequired'),
              },
            ]}
          >
            <Select
              options={[
                { value: UserType.Student, label: t<string>('userTypes.student') },
                { value: UserType.Teacher, label: t<string>('userTypes.teacher') },
                { value: UserType.PrivateTeacher, label: t<string>('userTypes.privateTeacher') },
                { value: UserType.PrivateSchoolOwner, label: t<string>('userTypes.privateSchoolOwner') },
              ]}
            />
          </Form.Item>

          <Form.Item label={boldLabel(t<string>('phoneNumber'))}>
            <Space.Compact block>
              <Form.Item
                name="phoneNumberPrefix"
                rules={[
                  {
                    required: Boolean(phoneNumber),
                    message: t<string>('common:validationErrors.fieldIsRequired'),
                  },
                ]}
                noStyle
              >
                <Select
                  style={{ width: '30%' }}
                  options={prefixes
                    .sort((a, b) => Number(a.value) - Number(b.value))
                    .map((prefix) => ({
                      value: `+${prefix.value}`,
                      label: (
                        <span>
                          <img
                            src={`https://flagcdn.com/w20/${prefix.country.toLowerCase()}.jpg`}
                            alt={`${prefix.value}`}
                            width="20"
                          />
                          {` +${prefix.value}`}
                        </span>
                      ),
                    }))}
                />
              </Form.Item>
              <Form.Item name="phoneNumber" noStyle>
                <InputNumber
                  style={{ width: '70%', borderLeft: 'none' }}
                  controls={false}
                  keyboard={false}
                />
              </Form.Item>
            </Space.Compact>
          </Form.Item>

          <Form.Item
            name="password"
            label={boldLabel(t<string>('password'))}
            rules={[
              {
                required: true,
                message: t<string>('common:validationErrors.fieldIsRequired'),
              },
              {
                min: 8,
                message: t<string>('common:validationErrors.minLength', { number: 8 }),
              },
            ]}
          >
            <Input.Password
              prefix={<LockOutlined style={prefixIconStyle} />}
              placeholder="••••••••"
              style={bigInput}
            />
          </Form.Item>

          <Form.Item
            name="passwordConfirmation"
            label={boldLabel(t<string>('passwordConfirmation'))}
            rules={[
              {
                required: true,
                message: t<string>('common:validationErrors.fieldIsRequired'),
              },
              ({ getFieldValue }) => ({
                validator(rule, value) {
                  if (!value || getFieldValue('password') === value) {
                    return Promise.resolve();
                  }

                  return Promise.reject(t<string>('common:validationErrors.passwordNotMatch'));
                },
              }),
            ]}
          >
            <Input.Password
              prefix={<LockOutlined style={prefixIconStyle} />}
              placeholder="••••••••"
              style={bigInput}
            />
          </Form.Item>

          <Form.Item
            name="termsAndPrivacyPolicy"
            valuePropName="checked"
            rules={[
              {
                validator: (_, value) =>
                  value
                    ? Promise.resolve()
                    : Promise.reject(t<string>('common:validationErrors.fieldIsRequired')),
              },
            ]}
          >
            <Checkbox>
              {t<string>('acceptTerms')}
              <a
                href={t<string>('common:links.terms')}
                target="_blank"
                rel="noreferrer nofollow"
                style={{ marginRight: 5 }}
              >
                {t<string>('termsOfService')}
              </a>
              {t<string>('acceptOr')}
              <a
                href={t<string>('common:links.privacyPolicy')}
                target="_blank"
                rel="noreferrer nofollow"
                style={{ marginRight: 5 }}
              >
                {t<string>('privacyPolicy')}
              </a>
              {t<string>('acceptEnd')}
            </Checkbox>
          </Form.Item>

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              block
              className={styles.submitButton}
              loading={loading}
            >
              {t<string>('common:button.register')}
            </Button>
          </Form.Item>
        </Form>

        <Divider />

        <div className={styles.register}>
          <h4 className={styles.registerClaim}>{t<string>('alreadyHaveAccount')}</h4>
          <Link to="/auth/login">{t<string>('common:button.login')}</Link>
        </div>
      </div>
    </div>
  );
};
